import React from "react";
import AnimationRevealPage from "../../../../helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import Header from "../../../../Components/headers/PageHeader";
import MainFeature1 from "../../../../Components/Features/TwoColWithButton.js";
import Features from "../../../../Components/Features/ThreeColumnPages";
import {
  FaVideo,
  FaStar,
  FaRocket,
  FaEye,
  FaUsers,
  FaCheckCircle,
} from "react-icons/fa";

// Styled Components
const Subheading = tw.span`uppercase tracking-wider text-sm`;
const Heading = tw.h1`text-2xl md:text-4xl lg:text-5xl font-bold`;
const Description = tw.p`text-base md:text-lg lg:text-xl`;

export const BrandVideoProductionInDubai = () => {
  return (
    <AnimationRevealPage>
      {/* Header Section */}
      <Header from="Brand Video Production in Dubai" />

      {/* Hero Section */}
      <MainFeature1
        subheading={<Subheading>Elevate Your Brand</Subheading>}
        heading={
          <Heading>
            Brand <span tw="text-primary-500">Video Production</span> in Dubai
          </Heading>
        }
        description={
          <Description>
            At MilaajBrandset, we offer professional brand video production
            services in **Dubai**, crafting cinematic, narrative-driven videos
            that amplify your brand’s identity and connect emotionally with
            audiences. From compelling brand stories to promotional films, we
            bring your vision to life with impact.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1589941013453-ec89f33b5e95?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Professional product photography
          "https://images.unsplash.com/photo-1580828343064-fde4fc206bc6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Video production setup
          "https://images.unsplash.com/photo-1584905066893-7d5c142ba4e1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Product showcase
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Tell Your Brand Story"
        buttonRounded={true}
        textOnLeft={false}
      />

      {/* Key Benefits Section */}
      <Features
        subheading={<Subheading>Why Choose Us?</Subheading>}
        heading="Premier Brand Video Production in Dubai"
        description="We create videos that define and elevate your brand."
        cards={[
          {
            icon: <FaVideo color="#ff5464" size={40} />,
            title: "Cinematic Storytelling",
            description: "Emotionally engaging narratives in high-definition.",
          },
          {
            icon: <FaStar color="#ff5464" size={40} />,
            title: "Trusted Expertise",
            description: "Proven success with leading Dubai brands.",
          },
          {
            icon: <FaRocket color="#ff5464" size={40} />,
            title: "Fast Turnaround",
            description: "Timely delivery for campaign launches.",
          },
          {
            icon: <FaEye color="#ff5464" size={40} />,
            title: "Audience Impact",
            description: "Videos designed to captivate and inspire.",
          },
          {
            icon: <FaUsers color="#ff5464" size={40} />,
            title: "Brand Alignment",
            description: "Customized to reflect your unique identity.",
          },
          {
            icon: <FaCheckCircle color="#ff5464" size={40} />,
            title: "Versatile Formats",
            description: "Brand films, promos, and social media content.",
          },
        ]}
      />

      {/* Our Process Section */}
      <MainFeature1
        subheading={<Subheading>How We Work</Subheading>}
        heading="Our Brand Video Production Process"
        description={
          <Description>
            We craft impactful brand videos with a streamlined process:
            <ul tw="mt-4 list-disc pl-6">
              <li>Discovery: Understand your brand’s story and goals.</li>
              <li>Concept: Develop scripts and storyboards that resonate.</li>
              <li>Filming: Use 4K equipment for stunning visuals.</li>
              <li>Editing: Enhance with motion graphics and sound design.</li>
              <li>Delivery: Provide polished videos ready for launch.</li>
              <li>Support: Offer revisions and campaign analytics.</li>
            </ul>
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Creative setup
          "https://images.unsplash.com/photo-1603796846097-bee99e4a601f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Product photography
          "https://images.unsplash.com/photo-1551269901-5c5e14c25df7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Professional camera equipment
        ]}
        widths={["60%", "55%", "70%"]}
        heights={["60%", "45%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "right: 30%; top: 10%;",
          "left: 35%; bottom: 20%;",
          "right: 32%; bottom: 10%;",
        ]}
        primaryButtonText="Learn More"
        buttonRounded={true}
        textOnLeft={true}
      />

      {/* Success Stories Section */}
      <Features
        subheading={<Subheading>Success Stories</Subheading>}
        heading="Brand Video Wins in Dubai"
        description="Our videos have transformed brand narratives."
        cards={[
          {
            icon: <FaVideo color="#ff5464" size={40} />,
            title: "Brand Launch",
            description: "Drove massive engagement for a new campaign.",
          },
          {
            icon: <FaEye color="#ff5464" size={40} />,
            title: "Audience Connection",
            description: "Boosted loyalty for a Dubai retailer.",
          },
          {
            icon: <FaStar color="#ff5464" size={40} />,
            title: "Client Satisfaction",
            description: "96% approval from brand video clients.",
          },
        ]}
      />

      {/* Call-to-Action Section */}
      <MainFeature1
        subheading={<Subheading>Ready to Define Your Brand?</Subheading>}
        heading="Brand Video Production in Dubai"
        description={
          <Description>
            Partner with MilaajBrandset for professional brand video production
            services in Dubai. Get cinematic, impactful videos that elevate your
            brand’s story—contact us today!
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1617040619263-41c5a9ca7521?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Video production
          "https://images.unsplash.com/photo-1616763864165-0a25fd5022df?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Professional filming setup
          "https://images.unsplash.com/photo-1585909695284-32d2985ac9c0?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Product showcase
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Contact Us Now"
        buttonRounded={true}
        textOnLeft={false}
      />
    </AnimationRevealPage>
  );
};
